import { useEffect, useState } from "react";
import { 
  Eye, EyeOff, Save, Clock, Image, Type, Calendar, 
  Link, Instagram, Twitter, Facebook, Youtube, Users,
  Mail, Trash2, Download, AlertTriangle, Lock
} from "lucide-react";
import { Button } from "../ui/button";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const getAuthHeader = () => {
  const token = localStorage.getItem('admin_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const ComingSoonTab = () => {
  const [settings, setSettings] = useState({
    enabled: false,
    title: "Coming Soon",
    subtitle: "We're working hard to bring you something amazing",
    description: "Our new platform is under construction. Sign up to be notified when we launch!",
    logo_url: "",
    background_image: "",
    show_countdown: false,
    launch_date: "",
    bypass_password: "",
    social_links: {
      instagram: "",
      twitter: "",
      facebook: "",
      youtube: ""
    }
  });
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSubscribers, setShowSubscribers] = useState(false);

  useEffect(() => {
    fetchSettings();
    fetchSubscribers();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/coming-soon`);
      if (response.data) {
        setSettings(prev => ({ 
          ...prev, 
          ...response.data,
          social_links: { ...prev.social_links, ...(response.data.social_links || {}) }
        }));
      }
    } catch (error) {
      console.error("Error fetching settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscribers = async () => {
    try {
      const response = await axios.get(`${API}/settings/coming-soon/subscribers`, {
        headers: getAuthHeader()
      });
      setSubscribers(response.data.subscribers || []);
    } catch (error) {
      console.error("Error fetching subscribers:", error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/coming-soon`, settings, {
        headers: getAuthHeader()
      });
      alert("Coming Soon settings saved successfully!");
    } catch (error) {
      console.error("Error saving settings:", error);
      alert("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const exportSubscribers = () => {
    const csv = "Email,Subscribed At\n" + 
      subscribers.map(s => `${s.email},${s.subscribed_at}`).join("\n");
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'launch_subscribers.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-heading uppercase text-white">Coming Soon Page</h2>
          <p className="text-sm text-zinc-500 mt-1">Configure the coming soon landing page</p>
        </div>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-orange-500 text-black hover:bg-orange-400 rounded-none px-6"
          data-testid="save-coming-soon"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? "Saving..." : "Save Changes"}
        </Button>
      </div>

      {/* Enable Toggle */}
      <div className="bg-zinc-900 border border-white/10 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {settings.enabled ? (
              <div className="w-12 h-12 bg-orange-500 flex items-center justify-center">
                <Eye className="w-6 h-6 text-black" />
              </div>
            ) : (
              <div className="w-12 h-12 bg-zinc-800 flex items-center justify-center">
                <EyeOff className="w-6 h-6 text-zinc-500" />
              </div>
            )}
            <div>
              <h3 className="text-lg font-bold text-white">Coming Soon Mode</h3>
              <p className="text-sm text-zinc-500">
                {settings.enabled 
                  ? "Site visitors will see the Coming Soon page" 
                  : "Site is live and accessible to all visitors"}
              </p>
            </div>
          </div>
          <button
            onClick={() => setSettings({ ...settings, enabled: !settings.enabled })}
            className={`relative w-14 h-7 rounded-full transition-colors ${
              settings.enabled ? 'bg-orange-500' : 'bg-zinc-700'
            }`}
            data-testid="toggle-coming-soon"
          >
            <div className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-transform ${
              settings.enabled ? 'translate-x-8' : 'translate-x-1'
            }`} />
          </button>
        </div>

        {settings.enabled && (
          <div className="mt-4 p-3 bg-orange-500/10 border border-orange-500/30 text-orange-400 text-sm flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Coming Soon mode is ACTIVE. All visitors will see the Coming Soon page instead of the main site.
          </div>
        )}
      </div>

      {/* Content Settings */}
      <div className="bg-zinc-900 border border-white/10 p-6 space-y-6">
        <h3 className="text-lg font-bold text-white flex items-center gap-2">
          <Type className="w-5 h-5 text-orange-500" />
          Page Content
        </h3>

        <div className="grid gap-4">
          <div>
            <label className="block text-xs font-bold uppercase text-zinc-400 mb-2">Title</label>
            <input
              type="text"
              value={settings.title}
              onChange={(e) => setSettings({ ...settings, title: e.target.value })}
              placeholder="Coming Soon"
              className="w-full bg-black border border-white/10 text-white px-4 py-2"
              data-testid="coming-soon-title"
            />
          </div>

          <div>
            <label className="block text-xs font-bold uppercase text-zinc-400 mb-2">Subtitle</label>
            <input
              type="text"
              value={settings.subtitle}
              onChange={(e) => setSettings({ ...settings, subtitle: e.target.value })}
              placeholder="We're working hard to bring you something amazing"
              className="w-full bg-black border border-white/10 text-white px-4 py-2"
              data-testid="coming-soon-subtitle"
            />
          </div>

          <div>
            <label className="block text-xs font-bold uppercase text-zinc-400 mb-2">Description</label>
            <textarea
              value={settings.description}
              onChange={(e) => setSettings({ ...settings, description: e.target.value })}
              placeholder="Our new platform is under construction..."
              rows={3}
              className="w-full bg-black border border-white/10 text-white px-4 py-2 resize-none"
              data-testid="coming-soon-description"
            />
          </div>
        </div>
      </div>

      {/* Visual Settings */}
      <div className="bg-zinc-900 border border-white/10 p-6 space-y-6">
        <h3 className="text-lg font-bold text-white flex items-center gap-2">
          <Image className="w-5 h-5 text-orange-500" />
          Visual Settings
        </h3>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-bold uppercase text-zinc-400 mb-2">Logo URL</label>
            <input
              type="text"
              value={settings.logo_url}
              onChange={(e) => setSettings({ ...settings, logo_url: e.target.value })}
              placeholder="https://example.com/logo.png"
              className="w-full bg-black border border-white/10 text-white px-4 py-2"
            />
            {settings.logo_url && (
              <div className="mt-2 p-2 bg-zinc-800 border border-zinc-700">
                <img src={settings.logo_url} alt="Logo preview" className="h-12 object-contain" />
              </div>
            )}
          </div>

          <div>
            <label className="block text-xs font-bold uppercase text-zinc-400 mb-2">Background Image URL</label>
            <input
              type="text"
              value={settings.background_image}
              onChange={(e) => setSettings({ ...settings, background_image: e.target.value })}
              placeholder="https://example.com/background.jpg"
              className="w-full bg-black border border-white/10 text-white px-4 py-2"
            />
            {settings.background_image && (
              <div className="mt-2 p-2 bg-zinc-800 border border-zinc-700">
                <img src={settings.background_image} alt="Background preview" className="h-20 w-full object-cover" />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Countdown Settings */}
      <div className="bg-zinc-900 border border-white/10 p-6 space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <Clock className="w-5 h-5 text-orange-500" />
            Countdown Timer
          </h3>
          <button
            onClick={() => setSettings({ ...settings, show_countdown: !settings.show_countdown })}
            className={`relative w-12 h-6 rounded-full transition-colors ${
              settings.show_countdown ? 'bg-orange-500' : 'bg-zinc-700'
            }`}
          >
            <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
              settings.show_countdown ? 'translate-x-6' : 'translate-x-0.5'
            }`} />
          </button>
        </div>

        {settings.show_countdown && (
          <div>
            <label className="block text-xs font-bold uppercase text-zinc-400 mb-2">Launch Date</label>
            <input
              type="datetime-local"
              value={settings.launch_date}
              onChange={(e) => setSettings({ ...settings, launch_date: e.target.value })}
              className="w-full md:w-auto bg-black border border-white/10 text-white px-4 py-2"
            />
          </div>
        )}
      </div>

      {/* Bypass Password */}
      <div className="bg-zinc-900 border border-white/10 p-6 space-y-6">
        <h3 className="text-lg font-bold text-white flex items-center gap-2">
          <Lock className="w-5 h-5 text-orange-500" />
          Bypass Password
        </h3>
        
        <p className="text-sm text-zinc-500">
          Set a password to allow specific users to bypass the Coming Soon page and access the full site.
        </p>
        
        <div>
          <label className="block text-xs font-bold uppercase text-zinc-400 mb-2">Access Password</label>
          <input
            type="text"
            value={settings.bypass_password || ""}
            onChange={(e) => setSettings({ ...settings, bypass_password: e.target.value })}
            placeholder="Leave empty to disable bypass"
            className="w-full md:w-1/2 bg-black border border-white/10 text-white px-4 py-2"
            data-testid="bypass-password-input"
          />
          <p className="text-xs text-zinc-600 mt-2">
            Users can click "Have an access code?" on the Coming Soon page to enter this password.
          </p>
        </div>
      </div>

      {/* Social Links */}
      <div className="bg-zinc-900 border border-white/10 p-6 space-y-6">
        <h3 className="text-lg font-bold text-white flex items-center gap-2">
          <Link className="w-5 h-5 text-orange-500" />
          Social Links
        </h3>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="flex items-center gap-3">
            <Instagram className="w-5 h-5 text-zinc-500" />
            <input
              type="text"
              value={settings.social_links.instagram || ""}
              onChange={(e) => setSettings({ 
                ...settings, 
                social_links: { ...settings.social_links, instagram: e.target.value }
              })}
              placeholder="Instagram URL"
              className="flex-1 bg-black border border-white/10 text-white px-4 py-2"
            />
          </div>

          <div className="flex items-center gap-3">
            <Twitter className="w-5 h-5 text-zinc-500" />
            <input
              type="text"
              value={settings.social_links.twitter || ""}
              onChange={(e) => setSettings({ 
                ...settings, 
                social_links: { ...settings.social_links, twitter: e.target.value }
              })}
              placeholder="Twitter/X URL"
              className="flex-1 bg-black border border-white/10 text-white px-4 py-2"
            />
          </div>

          <div className="flex items-center gap-3">
            <Facebook className="w-5 h-5 text-zinc-500" />
            <input
              type="text"
              value={settings.social_links.facebook || ""}
              onChange={(e) => setSettings({ 
                ...settings, 
                social_links: { ...settings.social_links, facebook: e.target.value }
              })}
              placeholder="Facebook URL"
              className="flex-1 bg-black border border-white/10 text-white px-4 py-2"
            />
          </div>

          <div className="flex items-center gap-3">
            <Youtube className="w-5 h-5 text-zinc-500" />
            <input
              type="text"
              value={settings.social_links.youtube || ""}
              onChange={(e) => setSettings({ 
                ...settings, 
                social_links: { ...settings.social_links, youtube: e.target.value }
              })}
              placeholder="YouTube URL"
              className="flex-1 bg-black border border-white/10 text-white px-4 py-2"
            />
          </div>
        </div>
      </div>

      {/* Email Subscribers */}
      <div className="bg-zinc-900 border border-white/10 p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <Mail className="w-5 h-5 text-orange-500" />
            Email Subscribers
            <span className="ml-2 px-2 py-0.5 bg-orange-500/20 text-orange-500 text-xs font-bold">
              {subscribers.length}
            </span>
          </h3>
          <div className="flex gap-2">
            <Button
              onClick={() => setShowSubscribers(!showSubscribers)}
              className="bg-zinc-800 text-white hover:bg-zinc-700 rounded-none text-xs px-3 py-1 h-auto"
            >
              <Users className="w-3 h-3 mr-1" />
              {showSubscribers ? "Hide" : "View"}
            </Button>
            {subscribers.length > 0 && (
              <Button
                onClick={exportSubscribers}
                className="bg-zinc-800 text-white hover:bg-zinc-700 rounded-none text-xs px-3 py-1 h-auto"
              >
                <Download className="w-3 h-3 mr-1" />
                Export CSV
              </Button>
            )}
          </div>
        </div>

        {showSubscribers && (
          <div className="max-h-60 overflow-y-auto">
            {subscribers.length === 0 ? (
              <p className="text-zinc-500 text-sm">No subscribers yet</p>
            ) : (
              <div className="space-y-2">
                {subscribers.map((sub, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 bg-zinc-800/50 text-sm">
                    <span className="text-white">{sub.email}</span>
                    <span className="text-zinc-500 text-xs">
                      {new Date(sub.subscribed_at).toLocaleDateString()}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Preview Link */}
      <div className="text-center">
        <a 
          href="/coming-soon-preview" 
          target="_blank" 
          className="text-orange-500 hover:text-orange-400 text-sm"
        >
          Preview Coming Soon Page →
        </a>
      </div>
    </div>
  );
};

export default ComingSoonTab;
